import React from 'react';
import { CheckoutConfig, CheckoutAddon, CheckoutField } from '../../../types';
import { CURRENCIES } from '../../../constants';

export { TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutTrustElements';

export interface FormFieldsProps {
  config: CheckoutConfig;
  formData: Record<string, string>;
  onInputChange: (fieldId: string, value: string) => void;
  inputClassName?: string;
  labelClassName?: string;
  customStyle?: React.CSSProperties;
}

export const FormFields: React.FC<FormFieldsProps> = ({
  config,
  formData,
  onInputChange,
  inputClassName = '',
  labelClassName = '',
  customStyle,
}) => {
  const isSolid = config.inputStyle === 'solid';
  const baseInputStyle = isSolid
    ? 'bg-slate-100 dark:bg-slate-800/80 border-transparent focus:bg-white dark:focus:bg-slate-900 focus:border-indigo-500'
    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:border-indigo-500';

  return (
    <div className="space-y-3">
      {config.customFields
        .filter((f) => f.isEnabled)
        .map((f) => (
          <div key={f.id} className="space-y-1 text-left">
            <label className={`block text-xs font-semibold text-slate-700 dark:text-slate-300 ${labelClassName}`}>
              {f.label}
              {f.required && <span className="text-rose-500 ml-0.5">*</span>}
            </label>
            <input
              type={f.type || 'text'}
              required={f.required}
              placeholder={f.placeholder}
              value={formData[f.id] || ''}
              onChange={(e) => onInputChange(f.id, e.target.value)}
              className={`w-full px-3.5 py-2.5 text-sm border rounded-lg transition-all outline-none ${baseInputStyle} ${inputClassName}`}
              style={{
                borderRadius: config.buttonRadius || '8px',
                ...customStyle,
              }}
            />
          </div>
        ))}
    </div>
  );
};

export const AddonsSelector: React.FC<{
  addons: CheckoutAddon[];
  selectedAddonIds: string[];
  onToggleAddon: (id: string) => void;
  currency?: string;
  primaryColor?: string;
}> = ({ addons, selectedAddonIds, onToggleAddon, currency = 'USD', primaryColor = '#4f46e5' }) => {
  const activeAddons = addons.filter((a) => a.isEnabled);
  if (activeAddons.length === 0) return null;

  return (
    <div className="space-y-2 pt-3 border-t border-slate-200/70 dark:border-slate-800">
      <p className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
        Add-on Upgrades
      </p>
      <div className="space-y-2">
        {activeAddons.map((addon) => {
          const isSelected = selectedAddonIds.includes(addon.id);
          return (
            <label
              key={addon.id}
              onClick={() => onToggleAddon(addon.id)}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                isSelected
                  ? 'border-indigo-500/80 bg-indigo-50/40 dark:bg-indigo-950/30'
                  : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 bg-white/50 dark:bg-slate-900/50'
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {}}
                className="mt-0.5 rounded text-indigo-600 focus:ring-indigo-500"
              />
              <div className="flex-1 text-left">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-900 dark:text-white">{addon.name}</span>
                  <span className="text-xs font-black text-indigo-600 dark:text-indigo-400">
                    +{CURRENCIES.find(c => c.code === (addon.currency || currency))?.symbol || '₹'}{addon.price}
                  </span>
                </div>
                {addon.description && (
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-tight">
                    {addon.description}
                  </p>
                )}
                {addon.features && addon.features.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {addon.features.map((feat, fIdx) => (
                      <span
                        key={fIdx}
                        className="inline-flex items-center text-[10px] font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded"
                      >
                        ✓ {feat}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export const CouponSection: React.FC<{
  config: CheckoutConfig;
  appliedCoupon: string | null;
  onApplyCoupon: (code: string) => void;
  onRemoveCoupon: () => void;
}> = ({ config, appliedCoupon, onApplyCoupon, onRemoveCoupon }) => {
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState(false);

  if (!config.allowCoupons) return null;

  const handleApply = () => {
    if (!code.trim()) return;
    if (code.toUpperCase() === 'INVALID') {
      setError(true);
      return;
    }
    setError(false);
    onApplyCoupon(code.trim().toUpperCase());
    setCode('');
  };

  return (
    <div className="pt-3 border-t border-slate-200/70 dark:border-slate-800 text-left">
      {appliedCoupon ? (
        <div className="flex items-center justify-between p-2.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-lg text-xs">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span className="font-bold text-emerald-800 dark:text-emerald-300">
              {appliedCoupon} ({config.couponDiscountPercentage || 15}% Discount Applied)
            </span>
          </div>
          <button
            type="button"
            onClick={onRemoveCoupon}
            className="text-emerald-700 dark:text-emerald-400 hover:text-rose-600 font-bold ml-2"
          >
            Remove
          </button>
        </div>
      ) : (
        <div className="space-y-1">
          <div className="flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleApply();
                }
              }}
              placeholder={config.couponPlaceholder || 'Enter promo code'}
              className="flex-1 px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 uppercase font-semibold outline-none focus:border-indigo-500"
            />
            <button
              type="button"
              onClick={handleApply}
              className="px-4 py-2 text-xs font-bold bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg hover:opacity-90 transition-opacity"
            >
              Apply
            </button>
          </div>
          {error && <p className="text-[10px] text-rose-500 font-semibold">Invalid promotional code</p>}
        </div>
      )}
    </div>
  );
};
